import React, { useContext, useState, useRef } from 'react';
import GSApi from '../../api/Api';
import { MainMenuItem, NavigationAllLanguages } from '../../types/Navigation';
import { useApp } from '../useApp/AppContext';

const useNavigationService = () => {
    const { appLanguage } = useApp();

    const [navigationData, setNavigationData] = useState<NavigationAllLanguages>(null);
    const [navigationDataLoaded, setNavigationDataLoaded] = useState<boolean>(false);
    const [navigationDataError, setNavigationDataError] = useState<any>(null);
    const [navigationDataLoading, setNavigationDataLoading] = useState<boolean>(false);
    const backgroundLoadingRef = useRef<boolean>(false);

    const loadRemainingLanguages = async (currentLang: string, existingData: NavigationAllLanguages) => {
        const allLanguages = ['en', 'et', 'ru'];
        const remainingLanguages = allLanguages.filter((lang) => lang !== currentLang);

        try {
            // Load remaining languages in parallel
            const remainingPromises = remainingLanguages.map((lang) => GSApi.fetchNavigation(lang));
            const remainingResponses = await Promise.all(remainingPromises);

            const completeData: NavigationAllLanguages = { ...existingData };

            remainingLanguages.forEach((lang, index) => {
                if (remainingResponses[index]?.response) {
                    completeData[lang] = remainingResponses[index].response;
                }
            });

            setNavigationData(completeData);
        } catch (error) {
            // if this happens: Silently fail for background loading - current language is already loaded :)
            console.warn('Failed to load remaining navigation languages:', error);
        } finally {
            backgroundLoadingRef.current = false;
        }
    };

    const loadNavigationData = async () => {
        if (navigationDataLoading) {
            return;
        }

        setNavigationDataLoaded(false);
        setNavigationDataLoading(true);
        setNavigationDataError(null);

        try {
            // Load current language first (priority)
            const currentLangResponse = await GSApi.fetchNavigation(appLanguage);

            if (currentLangResponse.error) {
                setNavigationDataError(currentLangResponse.error);
                setNavigationDataLoading(false);
                return;
            }

            // Set current language data immediately and mark as loaded
            // This allows the UI to render the navigation without waiting for other languages
            const partialData: NavigationAllLanguages = {
                [appLanguage]: currentLangResponse.response,
            } as NavigationAllLanguages;

            setNavigationData(partialData);
            setNavigationDataLoaded(true);
            setNavigationDataLoading(false);

            if (!backgroundLoadingRef.current) {
                backgroundLoadingRef.current = true;
                loadRemainingLanguages(appLanguage, partialData);
            }
        } catch (error) {
            setNavigationDataError(error);
            setNavigationDataLoading(false);
        }
    };

    return {
        loadNavigationData,
        getNavigationItems: (): MainMenuItem[] => navigationData?.[appLanguage]?.menuItems ?? [],
        getNavigationDataAllLanguages: (): NavigationAllLanguages => navigationData,
        navigationDataLoaded,
        navigationDataError,
    };
};

export const NavigationContext = React.createContext<ReturnType<typeof useNavigationService>>(null);
export const useNavigationData = () => useContext(NavigationContext);

export const NavigationProvider = ({ children }) => {
    const context = useNavigationService();

    return <NavigationContext.Provider value={context}>{children}</NavigationContext.Provider>;
};
